import { createClient } from '@supabase/supabase-js'

// Get environment variables - require them to be set properly
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing required Supabase environment variables')
}

// Extract project ID from URL for debugging
const projectId = supabaseUrl?.split('https://')[1]?.split('.supabase.co')[0] || 'unknown'

// Log when supabase client is initialized (helps debug env variable issues)
console.log('Initializing Supabase client with URL:', 
  supabaseUrl.substring(0, 15) + '...')
console.log('Project ID:', projectId)

// Custom storage implementation with validation
const customStorage = typeof window !== 'undefined' ? {
  getItem: (key: string) => {
    try {
      const value = window.localStorage.getItem(key)
      return value
    } catch (error) {
      console.error('🚨 SUPABASE: Storage GET error:', error)
      return null
    }
  },
  setItem: (key: string, value: string) => {
    try {
      window.localStorage.setItem(key, value)
      
      // Validate the item was actually stored
      const stored = window.localStorage.getItem(key)
      if (!stored) {
        console.error('🚨 SUPABASE: Storage SET failed - Item not found after setting!')
      } else if (stored !== value) {
        console.error('🚨 SUPABASE: Storage SET failed - Item value mismatch!')
      }
    } catch (error) {
      console.error('🚨 SUPABASE: Storage SET error:', error)
    }
  },
  removeItem: (key: string) => {
    try {
      window.localStorage.removeItem(key)
    } catch (error) {
      console.error('🚨 SUPABASE: Storage REMOVE error:', error)
    }
  }
} : undefined

// Create Supabase client with enhanced session persistence configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Use custom storage with debugging
    storage: customStorage,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Add additional session configuration
    storageKey: `sb-${projectId}-auth-token`,
    flowType: 'pkce'
  },
  // Configure for better reliability
  global: {
    headers: {
      'x-client-info': 'open-world-tours-frontend'
    }
  }
})

// Add session event monitoring for debugging
if (typeof window !== 'undefined') {
  supabase.auth.onAuthStateChange((event, session) => {
    console.log(`🔐 SUPABASE: Auth state change - Event: ${event}`)
    console.log(`🔐 SUPABASE: Session exists: ${!!session}`)
    if (session) {
      console.log(`🔐 SUPABASE: User ID: ${session.user?.id}`)
      console.log(`🔐 SUPABASE: Access token length: ${session.access_token?.length}`)
      console.log(`🔐 SUPABASE: Expires at: ${session.expires_at}`)
    }
    
    // Validate session storage after state change
    setTimeout(() => {
      const expectedKey = `sb-${projectId}-auth-token`
      const storedSession = window.localStorage.getItem(expectedKey)
      if (session && !storedSession) {
        console.error('🚨 SUPABASE: Session exists but not stored in localStorage!')
      } else if (!session && storedSession) {
        console.warn('⚠️ SUPABASE: No session but localStorage still has data')
      }
    }, 100)
  })
}

// Database types
export interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  bio?: string
  social_links?: {[key: string]: string}
  avatar_url?: string
  avatar_data?: any
  created_at: string
  updated_at: string
}

export interface Quest {
  id: string
  title: string
  description?: string
  quest_type: 'main' | 'side'
  difficulty: 'easy' | 'medium' | 'hard'
  points: number
  location_name?: string
  latitude?: number
  longitude?: number
  instructions?: string
  completion_criteria?: string
  order_index: number
  active: boolean
  created_at: string
}

export interface UserQuestProgress {
  id: string
  user_id: string
  quest_id: string
  status: 'not_started' | 'in_progress' | 'completed'
  progress_data: any
  completion_date?: string
  points_earned: number
  created_at: string
  updated_at: string
  quest?: Quest
}

export interface Achievement {
  id: string
  title: string
  description?: string
  icon?: string
  category: string
  points_required: number
  criteria: any
  active: boolean
  created_at: string
}

export interface UserAchievement {
  id: string
  user_id: string
  achievement_id: string
  earned_date: string
  achievement?: Achievement
}

export interface MobileSession {
  id: string
  user_id: string
  connection_token: string
  device_info?: any
  status: 'pending' | 'connected' | 'expired'
  created_at: string
  expires_at: string
}

// Auth helper functions
export const auth = {
  signUp: async (email: string, password: string, firstName: string, lastName: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
        },
      },
    })
    return { data, error }
  },

  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  getUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser()
    return { user, error }
  },

  getSession: async () => {
    const { data: { session }, error } = await supabase.auth.getSession()
    return { session, error }
  },
}

// Database helper functions
export const db = {
  // Users
  getProfile: async (userId: string) => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single()
    return { data, error }
  },

  updateProfile: async (userId: string, updates: Partial<User>) => {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()
    return { data, error }
  },

  // User Quest Progress (simplified for standalone quests)
  getUserQuestProgress: async (userId: string) => {
    const { data, error } = await supabase
      .from('user_quest_progress')
      .select(`
        *,
        quest:quests(*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  updateQuestProgress: async (
    userId: string,
    questId: string,
    status: 'not_started' | 'in_progress' | 'completed',
    progressData?: any,
    pointsEarned?: number
  ) => {
    const updates: any = {
      status,
      updated_at: new Date().toISOString(),
    }

    if (progressData) {
      updates.progress_data = progressData
    }

    if (status === 'completed') {
      updates.completion_date = new Date().toISOString()
      if (pointsEarned) {
        updates.points_earned = pointsEarned
      }
    }

    const { data, error } = await supabase
      .from('user_quest_progress')
      .upsert({
        user_id: userId,
        quest_id: questId,
        ...updates,
      })
      .select()
      .single()
    return { data, error }
  },

  // Achievements
  getAchievements: async () => {
    const { data, error } = await supabase
      .from('achievements')
      .select('*')
      .eq('active', true)
      .order('category')
      .order('points_required')
    return { data, error }
  },

  getUserAchievements: async (userId: string) => {
    const { data, error } = await supabase
      .from('user_achievements')
      .select(`
        *,
        achievement:achievements(*)
      `)
      .eq('user_id', userId)
      .order('earned_date', { ascending: false })
    return { data, error }
  },

  awardAchievement: async (userId: string, achievementId: string) => {
    const { data, error } = await supabase
      .from('user_achievements')
      .insert({
        user_id: userId,
        achievement_id: achievementId,
      })
      .select()
      .single()
    return { data, error }
  },

  // Mobile Sessions
  createMobileSession: async (sessionData: Omit<MobileSession, 'id' | 'created_at'>) => {
    const { data, error } = await supabase
      .from('mobile_sessions')
      .insert(sessionData)
      .select()
      .single()
    return { data, error }
  },

  getMobileSession: async (sessionId: string) => {
    const { data, error } = await supabase
      .from('mobile_sessions')
      .select('*')
      .eq('id', sessionId)
      .single()
    return { data, error }
  },

  getUserMobileSessions: async (userId: string) => {
    const { data, error } = await supabase
      .from('mobile_sessions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  updateMobileSessionStatus: async (sessionId: string, status: MobileSession['status'], deviceInfo?: any) => {
    const updates: any = { status, updated_at: new Date().toISOString() }
    if (deviceInfo) {
      updates.device_info = deviceInfo
    }

    const { data, error } = await supabase
      .from('mobile_sessions')
      .update(updates)
      .eq('id', sessionId)
      .select()
      .single()
    return { data, error }
  },

  disconnectMobileSession: async (sessionId: string) => {
    const { data, error } = await supabase
      .from('mobile_sessions')
      .update({ status: 'expired' })
      .eq('id', sessionId)
      .select()
      .single()
    return { data, error }
  },

  // Find mobile session by connection token
  findMobileSessionByToken: async (connectionToken: string) => {
    const { data, error } = await supabase
      .from('mobile_sessions')
      .select('*')
      .eq('connection_token', connectionToken)
      .eq('status', 'pending')
      .gte('expires_at', new Date().toISOString())
      .single()
    return { data, error }
  },
}